// VIBRANIAM-SPACE v2 - ImageStrobeSubstrate.js
// Substrate for Image Strobe node: displays a sequence of images covering the viewport without deformation.

import * as THREE from 'three';
import { loadFotostroboManifest } from '../fotostrobo.js';

const DEFAULT_PARAMS = {
  source: 'user',      // 'user' (loaded via picker) or 'library' (fotostrobo manifest)
  images: [],          // array of { url: string, weight: number, credit?: string }
  mode: 'sequential',  // 'sequential' or 'random'
  speed: 1.0,          // Hz, hard-clamped to 3
  confirmed: false,    // fotosensitivity warning acknowledged
};

// Hard clamp: alternation speed must NEVER exceed 3 Hz, even when a
// modulation source drives the `speed` port above its slider max.
const MAX_SPEED_HZ = 3;

export class ImageStrobeSubstrate {
  constructor(params = {}) {
    this.params = { ...DEFAULT_PARAMS, ...params };
    this.textures = [];
    this.credits = [];
    this.currentTextureIndex = 0;
    this.timer = 0;
    this.scene = new THREE.Scene();
    this.plane = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial()
    );
    this.scene.add(this.plane);
    // Expose points for Engine's buildLayer
    this.points = this.plane;
    this.viewportWidth = 1;
    this.viewportHeight = 1;
    this.loadingManager = new THREE.LoadingManager();
    this.textureLoader = new THREE.TextureLoader(this.loadingManager);
    this._imagesKey = '';
    this._libraryPromise = null;
    this._applySource(this.params);
  }

  setViewport(width, height) {
    this.viewportWidth = width;
    this.viewportHeight = height;
    this._updateScale();
  }

  setViewportHeight(height) {
    this.setViewport(this.viewportWidth, height);
  }

  reset() {
    this.timer = 0;
    this.currentTextureIndex = 0;
    if (this.textures.length) {
      this.material.map = this.textures[0];
      this._updateScale();
    } else {
      this.material.map = null;
    }
  }

  _updateScale() {
    if (!this.plane) return;
    const texture = this.textures[this.currentTextureIndex];
    if (texture && texture.image && texture.image.width && texture.image.height) {
      const imgAspect = texture.image.width / texture.image.height;
      const scaleX = this.viewportWidth / imgAspect;
      const scaleY = this.viewportHeight;
      const scale = Math.max(scaleX, scaleY);
      this.plane.scale.set(scale, scale, 1);
    } else {
      this.plane.scale.set(1, 1, 1);
    }
  }

  setParams(params) {
    const next = { ...this.params, ...params };
    const sourceChanged = next.source !== this.params.source;
    const imagesChanged = this._imagesKey !== this._imagesKeyOf(next.images);
    this.params = next;
    if (sourceChanged || imagesChanged) this._applySource(next);
  }

  _imagesKeyOf(images) {
    if (!images) return '';
    return images.map((i) => `${i.url}|${i.weight}`).join(',');
  }

  _applySource(params) {
    if (params.source === 'library') {
      this._loadLibrary();
    } else {
      this._loadImages(params.images || []);
    }
  }

  async _loadLibrary() {
    if (!this._libraryPromise) {
      this._libraryPromise = loadFotostroboManifest()
        .then((manifest) => manifest.images || [])
        .catch(() => []);
    }
    const images = await this._libraryPromise;
    // Only apply if we are still on the library source.
    if (this.params.source !== 'library') return;
    this._loadImages(images);
  }

  _loadImages(imageSpecs) {
    // Dispose old textures
    this.textures.forEach((t) => t.dispose());
    this.textures = [];
    this.credits = [];
    this.currentTextureIndex = 0;
    this.timer = 0;
    this._imagesKey = this._imagesKeyOf(imageSpecs);

    if (!imageSpecs || imageSpecs.length === 0) {
      this.material.map = null;
      return;
    }

    // Build a weighted sequence: `weight` is the number of times an image
    // repeats in the cycle, never a display duration (every flash is equal).
    const sequence = [];
    imageSpecs.forEach((spec) => {
      const weight = Math.max(1, Math.floor(Number(spec.weight) || 1));
      for (let i = 0; i < weight; i++) sequence.push(spec);
    });

    if (sequence.length === 0) {
      this.material.map = null;
      return;
    }

    this.credits = sequence.map((s) => s.credit || '');
    this.textures = sequence.map((spec) => this.textureLoader.load(spec.url));
    // Show the first frame immediately instead of waiting for the first tick.
    this.material.map = this.textures[0];
    this.material.needsUpdate = true;
    this._updateScale();
  }

  /** Current texture, exposed so the Mixer/Switcher can reuse it. */
  getCurrentTexture() {
    return this.textures[this.currentTextureIndex] || null;
  }

  setAudio() {
    // Not used
  }

  step(dt) {
    if (!this.params.confirmed || this.textures.length === 0) {
      this.material.map = null;
      return;
    }

    this.timer += dt;
    const speed = Math.min(Number(this.params.speed) || 0, MAX_SPEED_HZ);
    const interval = speed > 0 ? 1 / speed : Infinity;
    if (this.timer >= interval) {
      this.timer = 0;
      if (this.params.mode === 'random') {
        this.currentTextureIndex = Math.floor(Math.random() * this.textures.length);
      } else {
        this.currentTextureIndex = (this.currentTextureIndex + 1) % this.textures.length;
      }
      this.material.map = this.textures[this.currentTextureIndex];
      this.material.needsUpdate = true;
      this._updateScale();
    }
  }

  dispose() {
    this.textures.forEach((t) => t.dispose());
    this.textures = [];
  }
}
